// utils/storage.ts
interface Stored<T> {
  value: T;
  expires: number; // timestamp em ms (Date.now())
}

// true se o valor bruto parseia como o nosso formato Stored (objeto com `expires` numérico)
const isStored = (raw: string): { expires: number } | null => {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && typeof parsed.expires === 'number') {
      return parsed;
    }
  } catch {
    // não é JSON / não é nosso formato
  }
  return null;
};

// Libera espaço quando o localStorage estoura a quota.
// Remove só o que é seguro: nossas chaves já expiradas e as buscas ultima_consulta:*
// (TTL de 60s, sempre descartáveis) — nunca toca em chave de terceiro.
const evictOnQuota = (currentKey: string) => {
  const toRemove: string[] = [];
  // colete primeiro (removeItem no meio do loop pula índices)
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    if (key.startsWith('ultima_consulta:') && key !== currentKey) {
      toRemove.push(key);
      continue;
    }
    const raw = localStorage.getItem(key);
    if (!raw) continue;
    const stored = isStored(raw);
    if (stored && Date.now() > stored.expires) {
      toRemove.push(key);
    }
  }
  toRemove.forEach((key) => localStorage.removeItem(key));
};

/**
 * Salva algo no localStorage com prazo de validade (TTL em milissegundos).
 * Cache é otimização: se a quota estourar, tenta liberar espaço e nunca lança.
 */
export const setWithTTL = <T>(key: string, value: T, ttl: number) => {
  const record: Stored<T> = { value, expires: Date.now() + ttl };
  const payload = JSON.stringify(record);
  try {
    localStorage.setItem(key, payload);
  } catch {
    // quota estourou (ou storage indisponível): varre o lixo e tenta 1x mais
    try {
      evictOnQuota(key);
      localStorage.setItem(key, payload);
    } catch {
      // sem storage ou ainda sem espaço? desiste em silêncio — cache é opcional
    }
  }
};

/**
 * Lê um item; se estiver expirado, remove e devolve null
 */
export const getWithTTL = <T>(key: string): T | null => {
  let raw: string | null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    // SecurityError em contexto sem storage
    return null;
  }
  if (!raw) return null;

  try {
    const record = JSON.parse(raw) as Stored<T>;
    if (Date.now() > record.expires) {
      localStorage.removeItem(key);
      return null;
    }
    return record.value;
  } catch {
    // JSON quebrado? remove por segurança
    localStorage.removeItem(key);
    return null;
  }
};
